import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

import { jwtUtil } from '../utils'

const SharedStateContext = React.createContext()

const initialValue = {
  useDarkMode: false,
  user: null,
  showUserModal: false,
  tokenState: true
}

const sharedStateReducer = (state, action) => {
  switch (action.type) {
    case 'userChange':
      return {
        ...state,
        user: action.user
      }

    case 'set': {
      return {
        ...state,
        ...action.payload
      }
    }

    case 'showMessage':
      return {
        ...state,
        message: action.payload
      }

    case 'showUserModal':
      return {
        ...state,
        showUserModal: true
      }

    case 'closeUserModal':
      return {
        ...state,
        showUserModal: false
      }

    case 'hideMessage':
      return {
        ...state,
        message: null
      }

    case 'login':
      localStorage.setItem('accessToken', action.payload.accessToken)
      localStorage.setItem('refreshToken', action.payload.refreshToken)

      return {
        ...state,
        user: jwtUtil.getUserFromToken(action.payload.accessToken)
      }

    case 'logout':
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')

      return { ...state, user: null }

    default: {
      throw new Error(`Unsupported action type: ${action.type}`)
    }
  }
}

export const SharedStateProvider = ({ children, ...props }) => {
  const [state, dispatch] = React.useReducer(sharedStateReducer, {
    ...initialValue
  })
  const value = React.useMemo(() => [state, dispatch], [state])

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken')
        const refreshToken = localStorage.getItem('refreshToken')

        if (!accessToken) {
          dispatch({ type: 'logout' })

          return
        }

        dispatch({
          type: 'login',
          payload: {
            accessToken,
            refreshToken
          }
        })
      } catch (error) {
        console.error(error)
      }
    }

    restoreSession()
  }, [])

  return (
    <SharedStateContext.Provider value={value} {...props}>
      {children}
    </SharedStateContext.Provider>
  )
}

SharedStateProvider.propTypes = {
  children: PropTypes.node
}

export const useSharedState = () => {
  const context = React.useContext(SharedStateContext)

  if (!context) {
    throw new Error(`useSharedState must be used within a SharedStateContext`)
  }

  const [state, dispatch] = context
  const setState = payload => dispatch({ type: 'set', payload })
  const showMessage = payload => dispatch({ type: 'showMessage', payload })
  const hideMessage = () => dispatch({ type: 'hideMessage' })
  const login = payload => dispatch({ type: 'login', payload })
  const logout = () => dispatch({ type: 'logout' })
  const showUserModal = () => dispatch({ type: 'showUserModal' })
  const closeUserModal = () => dispatch({ type: 'closeUserModal' })
  const saveAccess = payload => dispatch({ type: 'saveAccess', payload })

  return [
    state,
    {
      setState,
      showMessage,
      hideMessage,
      showUserModal,
      closeUserModal,
      saveAccess,
      login,
      logout
    }
  ]
}
